
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions, INLINE_SIDEBAR_COMPONENTS } from '@common-reducers/BtLayoutSidebarReducer';
import { Box, IconButton, Typography } from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import { appConfig } from "@common/config/app.config";
import NotesPanel from './notes-panel';
import { ExtensionImageProxyProvider } from '../ExtensionImageProxyProvider';
import TasksSideBarWrapper from '@/common/components/chat-messages/tasks-sidebar-wrapper';
import { BANNER_OFFSET_HEIGHT, BANNER_OFFSET_TOP } from '@common-components/dunning-banner/banner-offset';
import ChatScheduledMessages from '@/common/views/whatsapp-view/ChatScheduledMessages';
import RemindersSidebar from '@/common/views/whatsapp-view/RemindersSidebar';
import QuickAgentsSidebar from '@/common/views/whatsapp-view/QuickAgentsSidebar';
import { getChatDisplayNameForHeader } from '@/common/utils/display-name-utils';

const DEFAULT_WIDTH = 400;
const MAX_WIDTH = 800;
const STYLE_ID = 'bt-sidebar-push-styles';

// Inject CSS rules that will automatically apply to #main even when WhatsApp replaces it
function injectPushStyles(width: number, isRTL: boolean, isResizing: boolean) {
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement;
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;
        document.head.appendChild(styleEl);
    }

    const transition = isResizing ? 'none' : 'margin 0.3s ease-out, width 0.3s ease-out';
    const marginProp = isRTL ? 'margin-left' : 'margin-right';
    const resetMarginProp = isRTL ? 'margin-right' : 'margin-left';

    styleEl.textContent = `
        #main {
            ${marginProp}: ${width}px !important;
            ${resetMarginProp}: 0px !important;
            width: calc(100% - ${width}px) !important;
            transition: ${transition} !important;
        }
        #top-header {
            width: calc(100% - ${width}px) !important;
            transition: ${isResizing ? 'none' : 'width 0.3s ease-out'} !important;
            ${isRTL ? 'left: auto !important; right: 0 !important;' : 'left: 0 !important; right: auto !important;'}
        }
    `;
}

function removePushStyles() {
    const styleEl = document.getElementById(STYLE_ID);
    if (styleEl) {
        styleEl.remove();
    }
}

export default function InlineSidebar() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { isOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);
    const isSidebarOpen = isOpen && INLINE_SIDEBAR_COMPONENTS.includes(componentToShow);
    const selectedBoardId = useAppSelector(getSelectedBoardId);

    // Get current chat name for title
    const currentChat = useAppSelector((state) => state.WhatsAppReducer?.currentChat);
    const chatName = getChatDisplayNameForHeader(currentChat);

    // Generate sidebar title based on component and chat name
    const getSidebarTitle = () => {
        if (componentToShow === 'Reminders') {
            return t('teamInbox.reminders');
        }
        if (componentToShow === 'QuickAgents') {
            return t('teamInbox.quickAgents.sidebarTitle');
        }
        switch (componentToShow) {
            case 'ChatScheduledMessages':
                return chatName ? `${chatName} - ${t('teamInbox.scheduledMessages')}` : t('teamInbox.scheduledMessages');
            case 'Notes':
                return chatName ? `${chatName} - ${t('teamInbox.notes')}` : t('teamInbox.notes');
            case 'Tasks':
                return chatName ? `${chatName} - ${t('teamInbox.tasks')}` : t('teamInbox.tasks');
            default:
                return componentToShow;
        }
    };
    const whatsappDirection = useAppSelector((state) => state.UserReducer.userWhatsappDirection);
    const isRTL = whatsappDirection === 'rtl';
    const MIN_WIDTH = isRTL ? 420 : 300;

    const sidebarRef = useRef<HTMLDivElement>(null);
    const isResizingRef = useRef(false);

    // Initialize width from local storage
    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem('bt-inline-sidebar-width');
        return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
    });
    const sidebarWidthRef = useRef(sidebarWidth);
    const isRTLRef = useRef(isRTL);
    const isSidebarOpenRef = useRef(isSidebarOpen);
    const minWidthRef = useRef(MIN_WIDTH);

    useEffect(() => {
        sidebarWidthRef.current = sidebarWidth;
    }, [sidebarWidth]);

    useEffect(() => {
        isRTLRef.current = isRTL;
        minWidthRef.current = MIN_WIDTH;
    }, [isRTL, MIN_WIDTH]);

    useEffect(() => {
        isSidebarOpenRef.current = isSidebarOpen;
    }, [isSidebarOpen]);

    const startResizing = React.useCallback((mouseDownEvent: React.MouseEvent) => {
        mouseDownEvent.preventDefault();
        isResizingRef.current = true;
        document.body.style.cursor = 'ew-resize';
    }, []);

    useEffect(() => {
        // Only attach the document-level drag listeners while the sidebar
        // is actually open (isOpen && the shown component is an inline
        // sidebar). Previously these two listeners were registered
        // unconditionally ([] deps) and stayed on `document` for the whole
        // session even when the sidebar was closed and its drag handle was
        // never rendered — pure always-on churn. Re-arm on reopen via the
        // isSidebarOpen dep.
        if (!isSidebarOpen) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizingRef.current) return;

            // Calculate new width based on RTL/LTR
            // Sidebar is always on the same side: RTL=left, LTR=right
            let newWidth: number;
            if (isRTLRef.current) {
                // In RTL, sidebar is on left, so width is mouse X position
                newWidth = e.clientX;
            } else {
                // In LTR, sidebar is on right, so width is window width - mouse X
                newWidth = window.innerWidth - e.clientX;
            }

            if (newWidth < minWidthRef.current) newWidth = minWidthRef.current;
            if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;

            setSidebarWidth(newWidth);
            sidebarWidthRef.current = newWidth;

            // Update styles immediately during resize (with no transition)
            if (isSidebarOpenRef.current) {
                injectPushStyles(newWidth, isRTLRef.current, true);
            }
        };

        const handleMouseUp = () => {
            if (isResizingRef.current) {
                isResizingRef.current = false;
                document.body.style.cursor = '';
                const finalWidth = sidebarWidthRef.current;
                localStorage.setItem('bt-inline-sidebar-width', String(finalWidth));

                // Re-inject styles with transition enabled
                if (isSidebarOpenRef.current) {
                    injectPushStyles(finalWidth, isRTLRef.current, false);
                }
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            // Clear any in-progress resize flag so a close mid-drag can't
            // leave us stuck resizing after the listeners re-arm on reopen.
            isResizingRef.current = false;
        };
    }, [isSidebarOpen]);

    useEffect(() => {
        // Shift layout when sidebar is open (always pinned)
        if (isSidebarOpen) {
            // Use CSS injection - styles will automatically apply to #main even when WhatsApp replaces it
            injectPushStyles(sidebarWidth, isRTL, isResizingRef.current);
        } else {
            // Reset layout if closed
            removePushStyles();
        }

        return () => {
            // Cleanup on effect re-run
        };
    }, [isSidebarOpen, sidebarWidth, isRTL]);

    // Cleanup on unmount regardless of state
    useEffect(() => {
        return () => {
            removePushStyles();
        }
    }, [])


    if (!isSidebarOpen) return null;
    const sidebarOnLeft = isRTL;

    return (
        <div
            style={{
                position: 'fixed',
                // Start below the dunning banner (#852); both fall back to the full viewport when no banner shows.
                top: BANNER_OFFSET_TOP,
                ...(sidebarOnLeft ? { left: 0, right: 'auto' } : { right: 0, left: 'auto' }),
                height: BANNER_OFFSET_HEIGHT,
                width: `${sidebarWidth}px`,
                zIndex: 1300,
                backgroundColor: 'var(--color-surface, #fff)',
                display: 'flex',
                flexDirection: 'column',
                transition: isResizingRef.current ? 'none' : 'width 0.3s ease-out',
                ...(sidebarOnLeft ? { borderRight: '1px solid #e0e0e0' } : { borderLeft: '1px solid #e0e0e0' }),
                direction: 'ltr'
            }}
            ref={sidebarRef}
        >
            {/* Drag Handle */}
            <div
                onMouseDown={startResizing}
                style={{
                    position: 'absolute',
                    ...(sidebarOnLeft ? { right: -4, left: 'auto' } : { left: -4, right: 'auto' }),
                    top: 0,
                    bottom: 0,
                    width: '8px',
                    cursor: 'ew-resize',
                    zIndex: 10000,
                    backgroundColor: 'transparent',
                }}
            />

            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{getSidebarTitle()}</Typography>
                <Box>
                    {componentToShow === 'Tasks' && selectedBoardId && (
                        <IconButton
                            onClick={() => window.open(`${appConfig.APP_ENDPOINT}/tasks?boardID=${selectedBoardId}`, "_blank")}
                            title={t('teamInbox.openTasksInNewTab')}
                        >
                            <OpenInNew />
                        </IconButton>
                    )}
                    <IconButton onClick={() => dispatch(btLayoutSidebarActions.closeSidebar())}>
                        <Close />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {componentToShow === 'Notes' && (
                    <ExtensionImageProxyProvider>
                        <NotesPanel />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'Tasks' && (
                    <TasksSideBarWrapper hideHeader={true} />
                )}
                {componentToShow === 'ChatScheduledMessages' && (
                    <ExtensionImageProxyProvider>
                        <ChatScheduledMessages hideHeader={true} />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'Reminders' && (
                    <ExtensionImageProxyProvider>
                        <RemindersSidebar />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'QuickAgents' && (
                    <ExtensionImageProxyProvider>
                        <QuickAgentsSidebar />
                    </ExtensionImageProxyProvider>
                )}
            </Box>
        </div>
    );
}
